import { useState, useEffect } from 'react';
import { Alert, Form, Input, Row, Col, Select } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { NSPButton } from '@components/ui/NSPButton';
import { useOattVendorList } from '@hooks/useOattVendorList';
import styles from './OattNspAndVendorInfoPage.module.css';

const { Option } = Select;
const { TextArea } = Input;

type OattVendor = {
    oatt_vendor_info_id: number;
    customer_code: string | null;
    contact_name: string | null;
    company_name: string | null;
    address_line1: string | null;
    address_line2: string | null;
    address_line3: string | null;
    account_number: string | null;
    apply_hst: string | null;
    vendor_comment: string | null;
};

type VendorPanelProps = {
    vendor: OattVendor;
    onSaved: () => void;
};

export function VendorPanel({ vendor, onSaved }: VendorPanelProps) {
    const [form] = Form.useForm();
    const { handleUpdateVendor, updatingVendor } = useOattVendorList();
    const [feedback, setFeedback] = useState<{ status: 'success' | 'error'; message: string } | null>(null);

    useEffect(() => {
        form.setFieldsValue({
            contact_name: vendor.contact_name ?? '',
            company_name: vendor.company_name ?? '',
            address_line1: vendor.address_line1 ?? '',
            address_line2: vendor.address_line2 ?? '',
            address_line3: vendor.address_line3 ?? '',
            account_number: vendor.account_number ?? '',
            apply_hst: vendor.apply_hst ?? 'Yes',
            vendor_comment: vendor.vendor_comment ?? '',
        });
    }, [vendor, form]);

    const handleSave = async () => {
        setFeedback(null);
        try {
            const values = await form.validateFields();
            const result = await handleUpdateVendor({
                oatt_vendor_info_id: vendor.oatt_vendor_info_id,
                company_name: values.company_name,
                contact_name: values.contact_name || undefined,
                address_line1: values.address_line1 || undefined,
                address_line2: values.address_line2 || undefined,
                address_line3: values.address_line3 || undefined,
                account_number: values.account_number || undefined,
                apply_hst: values.apply_hst,
                vendor_comment: values.vendor_comment,
            });
            if (result?.success) {
                setFeedback({ status: 'success', message: 'Vendor updated successfully.' });
                onSaved();
            } else {
                throw new Error(result?.error || 'Failed to save');
            }
        } catch (err) {
            setFeedback({
                status: 'error',
                message: err instanceof Error ? err.message : 'Failed to save vendor.',
            });
        }
    };

    return (
        <Form form={form} layout="vertical" onFinish={handleSave}>
            {feedback && (
                <Alert
                    className={styles.feedbackAlert}
                    message={feedback.status === 'success' ? 'Success' : 'Error'}
                    description={feedback.message}
                    type={feedback.status}
                    showIcon
                    closable
                    onClose={() => setFeedback(null)}
                />
            )}



            <div className={styles.sectionTitle}>Contact Information</div>
            <Row gutter={24}>
                <Col xs={24} md={12}>
                    <Form.Item label="Contact Name" name="contact_name" rules={[{ required: true, message: 'Required' }]}>
                        <Input placeholder="Contact person name" />
                    </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                    <Form.Item label="Company Code">
                        <Input
                            value={vendor.customer_code ?? ''}
                            readOnly
                        />
                    </Form.Item>
                </Col>
            </Row>

            <div className={styles.sectionTitle}>Company Information</div>
            <Row gutter={24}>
                <Col xs={24} md={12}>
                    <Form.Item label="Company Name" name="company_name" rules={[{ required: true, message: 'Required' }]}>
                        <Input />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item label="Account Number" name="account_number" rules={[{ required: true, message: 'Required' }]}>
                        <Input placeholder="Purchase order / account number" />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={24}>
                <Col xs={24} md={8}>
                    <Form.Item label="Address Line 1" name="address_line1" rules={[{ required: true, message: 'Required' }]}>
                        <Input />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item label="Address Line 2" name="address_line2" rules={[{ required: true, message: 'Required' }]}>
                        <Input />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item label="Address Line 3" name="address_line3" rules={[{ required: true, message: 'Required' }]}>
                        <Input />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={24}>
                <Col xs={24} md={6}>
                    <Form.Item label="Apply HST to Invoice" name="apply_hst" rules={[{ required: true, message: 'Required' }]}>
                        <Select style={{ width: 90 }}>
                            <Option value="Yes">Yes</Option>
                            <Option value="No">No</Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={24} md={16} offset={2}>
                    <Form.Item label="Comment to display on the invoice (optional)" name="vendor_comment">
                        <TextArea autoSize={{ minRows: 1 }} placeholder="Optional comment — will appear at the bottom of the invoice" />
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item>
                <NSPButton htmlType="submit" icon={<SaveOutlined />} loading={updatingVendor}>
                    Save Changes
                </NSPButton>
            </Form.Item>
        </Form>
    );
}
